import React, {Fragment, useEffect, useState, useRef} from 'react';
import {Button, FormControl} from 'react-bootstrap';
import {ConfirmationDialog} from "../../Common/ConfirmationDialog";
import {Glyphicon} from "../../Common/GlyphIcon";
import {ObjectUtils} from "../../../Utils/ObjectUtils";
import {service as SavedQueryService} from "../../../Services/SavedQueryService";
import {SB_ENTER_KEY_CODE, SB_ESC_KEY_CODE} from "../../../Constants";
import {KeyboardUtils} from "../../../Utils/KeyboardUtils";
import {useDefaultErrorHandler} from "../../../Hooks/ErrorHooks";
import {CLEAR_QUERY} from "../../../Redux/Actions";
import {useDispatch} from "react-redux";
import {useChangeFavorite, useGetDefinition, useHasColumns} from "../../../Hooks/QueryToolHooks";
import {useQueryParams} from "../../../../../monitoring/src/Hooks/RouteHooks"

export const QueryToolTableTitle = props => {
    const dispatch = useDispatch();
    const defaultErrorHandler = useDefaultErrorHandler();
    const getDefinition = useGetDefinition();
    const changeFavorite = useChangeFavorite();
    const hasColumns = useHasColumns();
    const {savedQueryId} = useQueryParams();

    const favoriteInput = useRef(null);

    const [savedQueries, setSavedQueries] = useState([]);
    const [currentFavoriteId, setCurrentFavoriteId] = useState('');
    const [changed, setChanged] = useState(false);
    const [isChanging, setIsChanging] = useState(false);
    const [changedFavoriteId, setChangedFavoriteId] = useState(null);
    const [favoriteName, setFavoriteName] = useState('');
    const [showFavoriteModal, setShowFavoriteModal] = useState(false);
    const [showNewQuery, setShowNewQuery] = useState(false);
    const [showDelete, setShowDelete] = useState(false);

    const definition = getDefinition();

    useEffect(async () => {
        await loadData();
    }, [])

    useEffect(() => {
        const currentFavorite = findFavorite(currentFavoriteId);

        if (!currentFavorite || isChanging || changed || !definition.columns.length) {
            return;
        }

        // Strip columns that don't make sense for comparisons
        const currentDefinition = {
            ...currentFavorite.definition,
            localTimezone: undefined
        };
        const originalDefinition = {
            ...definition,
            localTimezone: undefined
        };

        currentDefinition.columns = currentDefinition.columns.map(x => {
            const fields = ['guid', 'name', 'title', 'filters', 'aggregate'];
            const result = {};

            fields.forEach(field => {
                if (x[field] !== undefined) result[field] = x[field]
            });

            return result;
        });

        if (!ObjectUtils.Compare(currentDefinition, originalDefinition)) {
            setChanged(true)
        }
    }, [currentFavoriteId, changed, isChanging, definition])


    useEffect(() => {
        if(isChanging) {
            loadFavorite(currentFavoriteId);
            setIsChanging(false);
        }
    }, [isChanging, currentFavoriteId]);

    const loadData = async () => {
        await getFavorites();
        if (savedQueryId) {
            changeFavoriteItems(savedQueryId);
        }
    }

    const getFavorites = async () => {
        await SavedQueryService.Get()
            .then(({result}) => {
                setSavedQueries(result);
                setChanged(false)
            });
    }

    const findFavorite = (id) => {
        return savedQueries.find(x => x.savedQueryId === Number(id))
    }

    const showAddFavorites = () => {
        setShowFavoriteModal(true);
        favoriteInput.current.focus()
    }

    const addFavorite = () => {
        SavedQueryService.CreateSavedQuery({name: favoriteName, definition})
            .then(({result}) => {
                setCurrentFavoriteId(result.savedQueryId);
                setSavedQueries([...savedQueries, result])
                setShowFavoriteModal(false);
                setFavoriteName('');
                changeFavorite(result, result?.definition, true);
            })
            .catch(defaultErrorHandler);
    }

    const updateFavorite = () => {
        const data = {
            ...findFavorite(currentFavoriteId),
            definition,
        };

        SavedQueryService.UpdateSavedQuery(data)
            .then(({result}) => {
                getFavorites();
            })
            .catch(defaultErrorHandler);
    }

    const deleteFavorite = () => {
        SavedQueryService.DeleteSavedQuery(currentFavoriteId)
            .then(({result}) => {
                setShowDelete(false);
                changeFavoriteItems('');
                getFavorites();
            })
            .catch((...args) => {
                setShowDelete(false);
                defaultErrorHandler(...args)
            });
    }

    const changeFavoriteItems = (value) => {
        setCurrentFavoriteId(value);
        setChangedFavoriteId(null);
        setChanged(false);
        setIsChanging(true);
    }

    const loadFavorite = (value) => {
        const favorite = findFavorite(value);
        const definition = favorite ? favorite.definition : null;

        changeFavorite(favorite, definition);
    }

    const newQuery = () => {
        setShowNewQuery(false);
        dispatch({type: CLEAR_QUERY});
        changeFavoriteItems('');
    }

    const isChanged = () => {
        return changed || (definition.columns.length && !currentFavoriteId)
    }

    const isShared = () => {
        return findFavorite(currentFavoriteId)?.isShared;
    }

    const renderModalBody = () => {
        return (
            <div>
                <label>Query Name</label>
                <FormControl
                    type="text"
                    value={favoriteName}
                    onChange={(e) => setFavoriteName(e.target.value)}
                    onKeyDown={(x) => {
                        if (favoriteName) {
                            KeyboardUtils.handleKeyPress(SB_ENTER_KEY_CODE, x, () => addFavorite());
                        }
                        KeyboardUtils.handleKeyPress(SB_ESC_KEY_CODE, x, () => setShowFavoriteModal(false));
                    }}
                    ref={favoriteInput}
                />
            </div>
        );
    }

    return (
        <Fragment>
            <ConfirmationDialog
                Body={renderModalBody()}
                Display={showFavoriteModal}
                OnConfirm={() => addFavorite()}
                OnClose={() => setShowFavoriteModal(false)}
                ConfirmationButtonMessage={"Save Favorite"}
                CancelButtonMessage={"Cancel"}
                DisableConfirmationButton={!favoriteName}
                enforceFocus={false}
            />
            <ConfirmationDialog
                Title="Discard Changes?"
                Body="You have unsaved changes, are you sure you want to discard changes and load another favorite?"
                Display={changedFavoriteId}
                OnConfirm={() => changeFavoriteItems(changedFavoriteId)}
                OnClose={() => setChangedFavoriteId(null)}
                CancelButtonMessage="Cancel"
                ConfirmationButtonMessage="Discard Changes"
            />
            <ConfirmationDialog
                Title="Discard Changes?"
                Body="You have unsaved changes, are you sure you want to discard changes and create a new query?"
                Display={showNewQuery}
                OnConfirm={() => newQuery()}
                OnClose={() => setShowNewQuery(false)}
                CancelButtonMessage="Cancel"
                ConfirmationButtonMessage="Discard Changes"
            />
            <ConfirmationDialog
                Title="Delete Favorite?"
                Body="Are you sure you want to permanently delete this favorite?"
                Display={showDelete}
                OnDelete={() => deleteFavorite()}
                OnClose={() => setShowDelete(false)}
                CancelButtonMessage="Cancel"
            />
            <div className="sb-query-table-header">
                <h2>Query</h2>
                <div className="sb-query-favorites">
                    {
                        hasColumns &&
                        <Button variant="default" onClick={() => isChanged() ? setShowNewQuery(true) : newQuery()}>
                            <Glyphicon glyph="file" />
                            New Query
                        </Button>
                    }
                    <FormControl
                        as="select"
                        value={currentFavoriteId}
                        onChange={(e) => {
                            if (changed) {
                                setChangedFavoriteId(e.target.value);
                            } else {
                                changeFavoriteItems(e.target.value);
                            }
                        }}
                    >
                        <option value={''}>Favorites</option>
                        {savedQueries.map(x => (
                            <option key={x.savedQueryId} value={x.savedQueryId}>
                                {x.name} {x.isShared ? ' ⭐️' : null }
                            </option>
                        ))}
                    </FormControl>
                    {
                        currentFavoriteId && changed && !isShared() &&
                        <Button variant="default" onClick={() => updateFavorite()}>
                            <Glyphicon glyph="save" />
                            Save Favorite
                        </Button>
                    }
                    {
                        currentFavoriteId &&
                        <Button variant="default" onClick={() => showAddFavorites()}>
                            <Glyphicon glyph="save" />
                            Save as New Favorite
                        </Button>
                    }
                    {
                        currentFavoriteId && !isShared() &&
                        <Button variant="default" onClick={() => setShowDelete(true)}>
                            <Glyphicon glyph="trash" />
                            Delete Favorite
                        </Button>
                    }
                    {
                        hasColumns && !currentFavoriteId &&
                        <Button variant="default" onClick={() => showAddFavorites()}>
                            <Glyphicon glyph="plus" />
                            Add to Favorites
                        </Button>
                    }
                </div>
            </div>
        </Fragment>

    );
}
